import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
} from '@mui/material'

const PREFIX = 'CO2StatusCard'

const classes = {
  card: `${PREFIX}-card`,
  cardContent: `${PREFIX}-cardContent`,
  header: `${PREFIX}-header`,
  parameterName: `${PREFIX}-parameterName`,
  statusChip: `${PREFIX}-statusChip`,
  valueContainer: `${PREFIX}-valueContainer`,
  value: `${PREFIX}-value`,
  unit: `${PREFIX}-unit`,
  progressContainer: `${PREFIX}-progressContainer`,
  progressBar: `${PREFIX}-progressBar`,
  progressLabel: `${PREFIX}-progressLabel`,
  statusGood: `${PREFIX}-statusGood`,
  statusFair: `${PREFIX}-statusFair`,
  statusPoor: `${PREFIX}-statusPoor`,
}

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.card}`]: {
    height: '140px',
    minWidth: '240px',
    width: '100%',
    maxWidth: '280px',
    margin: 0.5,
    display: 'flex',
    flexDirection: 'column',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: 2,
    overflow: 'visible',
    position: 'relative',
    background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08), 0 1px 4px rgba(0, 0, 0, 0.04)',
    '&:hover': {
      transform: 'translateY(-4px) scale(1.02)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
      borderColor: theme.palette.primary.main,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: 'inherit',
        padding: '1px',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}40, transparent)`,
        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        maskComposite: 'exclude',
        pointerEvents: 'none',
      },
    },
    [theme.breakpoints.down('lg')]: {
      height: '120px',
      minWidth: '240px',
      maxWidth: '280px',
      margin: 0.25,
    },
    animation: 'cardEnter 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards',
    '@keyframes cardEnter': {
      '0%': {
        opacity: 0,
        transform: 'translateY(20px) scale(0.95)',
      },
      '100%': {
        opacity: 1,
        transform: 'translateY(0) scale(1)',
      },
    },
  },

  [`& .${classes.cardContent}`]: {
    padding: 4,
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
    '&:last-child': {
      paddingBottom: 4,
    },
    [theme.breakpoints.down('lg')]: {
      padding: 3,
      '&:last-child': {
        paddingBottom: 3,
      },
    },
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 1,
    minHeight: '32px',
  },

  [`& .${classes.parameterName}`]: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    lineHeight: 1.2,
    fontFamily: 'Rubik, Helvetica Neue, Arial, sans-serif',
    [theme.breakpoints.down('lg')]: {
      fontSize: '0.8rem',
      letterSpacing: '0.6px',
    },
  },

  [`& .${classes.statusChip}`]: {
    height: '26px',
    fontSize: '0.75rem',
    fontWeight: 600,
    minWidth: '55px',
    borderRadius: '13px',
    fontFamily: 'Rubik, Helvetica Neue, Arial, sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15), 0 1px 4px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    '& .MuiChip-label': {
      padding: '0 12px',
      fontWeight: 'inherit',
    },
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), 0 2px 6px rgba(0, 0, 0, 0.15)',
    },
    [theme.breakpoints.down('lg')]: {
      height: '22px',
      fontSize: '0.7rem',
      minWidth: '50px',
      '& .MuiChip-label': {
        padding: '0 8px',
      },
    },
  },

  [`& .${classes.valueContainer}`]: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center',
    marginBottom: 1,
    marginTop: 0.5,
  },

  [`& .${classes.value}`]: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.1,
    fontSize: '1.5rem',
    fontFamily: 'Rubik, Helvetica Neue, Arial, sans-serif',
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.3rem',
    },
  },

  [`& .${classes.unit}`]: {
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
    marginTop: 0.5,
    fontFamily: 'Rubik, Helvetica Neue, Arial, sans-serif',
    letterSpacing: '0.3px',
    [theme.breakpoints.down('lg')]: {
      fontSize: '0.75rem',
    },
  },

  [`& .${classes.progressContainer}`]: {
    marginTop: 'auto',
    paddingTop: 1,
    paddingLeft: '5%',
    paddingRight: '5%',
    overflow: 'visible',
    position: 'relative',
  },

  [`& .${classes.progressLabel}`]: {
    marginTop: 0.5,
    textAlign: 'center',
  },

  [`& .${classes.statusGood}`]: {
    backgroundColor: '#39DD7B', // Pro-portal success green
    color: 'white',
  },

  [`& .${classes.statusFair}`]: {
    backgroundColor: '#FFE623', // Pro-portal warning yellow
    color: 'black', // Better contrast on yellow
  },

  [`& .${classes.statusPoor}`]: {
    backgroundColor: '#F96C6C', // Pro-portal error red
    color: 'white',
  },
}))

interface StatusCardProps {
  parameter: string
  value: number
  unit: string
  status: 'Good' | 'Fair' | 'Poor'
  label: string
  animateValue?: boolean
}

const StatusCard: React.FC<StatusCardProps> = ({ 
  parameter, 
  value, 
  unit, 
  status, 
  label,
  animateValue = false
}) => {
  const [displayValue, setDisplayValue] = useState(animateValue ? 0 : value)

  useEffect(() => {
    if (animateValue) {
      const interval = setInterval(() => {
        setDisplayValue((prev) => {
          if (prev < value) {
            return Math.min(prev + Math.ceil((value - prev) / 10), value)
          }
          return value
        })
      }, 50)
      
      return () => clearInterval(interval)
    }
  }, [value, animateValue])

  const getProgressPercentage = () => {
    // Stable progress calculation based on status and value
    const baseValue = animateValue ? displayValue : value;
    
    switch (status) {
      case 'Good':
        // Map good values (0-1000 ppm typically) to 25-55% range
        return Math.min(25 + (baseValue / 1000) * 30, 55);
      case 'Fair':
        // Map fair values (1000-2000 ppm typically) to 60-80% range  
        return Math.min(60 + ((baseValue - 1000) / 1000) * 20, 80);
      case 'Poor':
        // Map poor values (2000+ ppm) to 85-100% range
        return Math.min(85 + ((baseValue - 2000) / 1000) * 15, 100);
      default:
        return 50;
    }
  }

  const getStatusClass = () => {
    switch (status.toLowerCase()) {
      case 'good':
        return classes.statusGood
      case 'fair':
        return classes.statusFair
      case 'poor':
        return classes.statusPoor
      default:
        return ''
    }
  }

  const calculatedPercentage = getProgressPercentage()

  return (
    <StyledCard className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.header}>
          <Typography className={classes.parameterName}>
            {parameter}
          </Typography>
          <Chip
            label={status}
            size="small"
            className={`${classes.statusChip} ${getStatusClass()}`}
          />
        </Box>
        
        <Box className={classes.valueContainer}>
          <Typography variant="h4" className={classes.value}>
            {animateValue ? displayValue : value}
          </Typography>
          <Typography className={classes.unit}>
            {unit}
          </Typography>
        </Box>
        
        <Box className={classes.progressContainer}>
          {status === 'Poor' ? (
            // Custom red progress bar for poor status with dramatic glow
            <Box
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: '#ffebee',
                position: 'relative',
                overflow: 'visible',
                boxShadow: `
                  inset 0 1px 3px rgba(0, 0, 0, 0.1), 
                  0 0 0 1px #F96C6C20,
                  0 0 8px #F96C6C15,
                  0 -5px 12px #F96C6C10
                `,
                animation: 'containerControlledDangerGlow 2s ease-in-out infinite alternate',
                '@keyframes containerControlledDangerGlow': {
                  '0%': { 
                    boxShadow: `
                      inset 0 1px 3px rgba(0, 0, 0, 0.1), 
                      0 0 0 1px #F96C6C20,
                      0 0 8px #F96C6C15,
                      0 -5px 12px #F96C6C10
                    `,
                  },
                  '100%': { 
                    boxShadow: `
                      inset 0 1px 3px rgba(0, 0, 0, 0.1), 
                      0 0 0 2px #F96C6C30,
                      0 0 12px #F96C6C20,
                      0 -8px 18px #F96C6C15
                    `,
                  },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-25px',
                  left: '10%',
                  right: '10%',
                  height: '40px',
                  background: `radial-gradient(ellipse at bottom, #F96C6C15 0%, #F96C6C08 30%, transparent 70%)`,
                  borderRadius: '50%',
                  animation: 'controlledUpwardRadiation 2s ease-in-out infinite alternate',
                  pointerEvents: 'none',
                  zIndex: 1,
                  '@keyframes controlledUpwardRadiation': {
                    '0%': {
                      opacity: 0.3,
                      transform: 'scaleY(0.8) scaleX(0.9)',
                    },
                    '100%': {
                      opacity: 0.6,
                      transform: 'scaleY(1.1) scaleX(1.1)',
                    },
                  },
                },
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${calculatedPercentage}%`,
                  backgroundColor: '#F96C6C', // Pro-portal error red
                  borderRadius: 5,
                  position: 'relative',
                  zIndex: 2,
                  animation: 'controlledDangerGlow 2s ease-in-out infinite alternate',
                  '@keyframes controlledDangerGlow': {
                    '0%': { 
                      filter: 'brightness(1) saturate(1)',
                      transform: 'scale(1)',
                      boxShadow: `
                        0 0 4px #F96C6C60,
                        0 0 8px #F96C6C40,
                        0 0 12px #F96C6C25,
                        0 -8px 16px #F96C6C15
                      `,
                    },
                    '100%': { 
                      filter: 'brightness(1.3) saturate(1.2)',
                      transform: 'scale(1.02)',
                      boxShadow: `
                        0 0 6px #F96C6C80,
                        0 0 12px #F96C6C60,
                        0 0 18px #F96C6C40,
                        0 -12px 24px #F96C6C25
                      `,
                    },
                  },
                }}
              />
            </Box>
          ) : status === 'Good' ? (
            // Custom green progress bar for good status
            <Box
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: '#D4E1E8', // Pro-portal grey[200]
                position: 'relative',
                overflow: 'hidden',
                boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${calculatedPercentage}%`,
                  backgroundColor: '#39DD7B', // Pro-portal success green
                  borderRadius: 5,
                  position: 'relative',
                  animation: 'greenPulseGlow 2s ease-in-out infinite alternate',
                  '@keyframes greenPulseGlow': {
                    '0%': { 
                      filter: 'brightness(1)',
                      boxShadow: `0 0 2px #39DD7B`,
                    },
                    '100%': { 
                      filter: 'brightness(1.4)',
                      boxShadow: `0 0 15px #39DD7B, 0 0 25px #39DD7B40`,
                    },
                  },
                }}
              />
            </Box>
          ) : (
            // Fair status with shimmer wave animation
            <Box
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: '#D4E1E8', // Pro-portal grey[200]
                position: 'relative',
                overflow: 'hidden',
                boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${calculatedPercentage}%`,
                  borderRadius: 5,
                  position: 'relative',
                  background: `linear-gradient(90deg, #FFE623 0%, #FFE623 40%, rgba(255,255,255,0.8) 50%, #FFE623 60%, #FFE623 100%)`,
                  backgroundSize: '200% 100%',
                  animation: 'shimmerWave 5s ease-in-out infinite',
                  '@keyframes shimmerWave': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }}
              />
            </Box>
          )}
          <Box className={classes.progressLabel}>
            <Typography variant="caption">
              {label}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </StyledCard>
  )
}

// Multiple cards component
const IAQStatusCards: React.FC = () => {
  const cardsData = [
    { parameter: 'CO₂', value: 606, unit: 'ppm', status: 'Good' as const, label: 'Carbon Dioxide' },
    { parameter: 'PM2.5', value: 12, unit: 'μg/m³', status: 'Fair' as const, label: 'Fine Particles' },
    { parameter: 'VOC', value: 450, unit: 'ppb', status: 'Good' as const, label: 'Volatile Compounds' },
    { parameter: 'HUMIDITY', value: 65, unit: '%RH', status: 'Good' as const, label: 'Relative Humidity' },
    { parameter: 'TEMP', value: 22, unit: '°C', status: 'Good' as const, label: 'Temperature' },
    { parameter: 'RADON', value: 180, unit: 'Bq/m³', status: 'Fair' as const, label: 'Radon Level' },
  ]

  return (
    <Grid container spacing={3} sx={{ width: '100%', margin: 0, padding: 2 }}>
      {cardsData.map((card, index) => (
        <Grid 
          item 
          xs={12} 
          sm={6} 
          md={4} 
          lg={4} 
          xl={4} 
          key={card.parameter}
          sx={{ 
            display: { 
              xs: index < 3 ? 'flex' : 'none',  // Only show first 3 cards on mobile
              sm: index < 4 ? 'flex' : 'none',  // Show 4 cards on small screens
              md: 'flex'  // Show all 6 cards on medium and larger screens
            },
            justifyContent: 'center',
            minHeight: '160px'
          }}
        >
          <StatusCard
            parameter={card.parameter}
            value={card.value}
            unit={card.unit}
            status={card.status}
            label={card.label}
            animateValue={index === 0} // Only animate the first card (CO2)
          />
        </Grid>
      ))}
    </Grid>
  )
}

// Keep the original component for backward compatibility with cycling animation
const CO2StatusCard: React.FC = () => {
  // CO2 data states with their corresponding status
  const co2States = [
    { value: 606, status: 'Good' as const },
    { value: 954, status: 'Fair' as const },
    { value: 1287, status: 'Poor' as const }
  ]
  
  const [currentStateIndex, setCurrentStateIndex] = useState(0)
  const currentData = co2States[currentStateIndex]

  // Rotate through states every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStateIndex((prevIndex) => (prevIndex + 1) % co2States.length)
    }, 5000)

    return () => clearInterval(interval)
  }, [co2States.length])

  return <StatusCard 
    parameter="CO₂" 
    value={currentData.value} 
    unit="ppm" 
    status={currentData.status} 
    label="Carbon Dioxide" 
    animateValue={true} 
  />
}

export default CO2StatusCard
export { StatusCard, IAQStatusCards } 